import moment from 'moment';

import { isEolMoment } from 'utils/constants';
import {
  getTimelineDisplayDates,
  isSpecialDateStatus,
  type MomentDates,
  type TimelineDisplayDates
} from 'utils/timelineDisplayDates';

export type EolTimelineColumnCard = {
  projectType?: string;
  showOnTimeline?: boolean;
  pco?: boolean;
  dates?: MomentDates;
};

function resolveMonthFromDisplay(
  display: TimelineDisplayDates
): string | undefined {
  if (
    isSpecialDateStatus(display.dateStatus) &&
    !display.start &&
    !display.month &&
    !display.quarter
  ) {
    return undefined;
  }

  if (display.start) {
    return moment(display.start).format('MMM');
  }
  if (display.month) {
    return display.month.split(' ')[0];
  }
  return undefined;
}

/** Month token (e.g. `Jul`) for roadmap column placement; matches TimelineCard chip. */
export function resolveEolTimelineColumnMonth(
  card: EolTimelineColumnCard
): string | undefined {
  return resolveMonthFromDisplay(
    getTimelineDisplayDates({
      dates: card.dates,
      projectType: card.projectType ?? '',
      showOnTimeline: !!card.showOnTimeline,
      pco: card.pco === true
    })
  );
}

/**
 * Whether an EOL card belongs in the given timeline month column.
 * Returns `null` when the card is not EOL (caller should use other placement rules).
 */
export function eolTimelineColumnPlacement(
  card: EolTimelineColumnCard,
  columnMonthLabel: string,
  defaultMonth: string
): boolean | null {
  if (!isEolMoment(card?.projectType)) {
    return null;
  }

  const display = getTimelineDisplayDates({
    dates: card.dates,
    projectType: card.projectType ?? '',
    showOnTimeline: !!card.showOnTimeline,
    pco: card.pco === true
  });

  if (
    isSpecialDateStatus(display.dateStatus) &&
    !display.start &&
    !display.month &&
    !display.quarter
  ) {
    return false;
  }

  const cardMonth = resolveMonthFromDisplay(display) ?? defaultMonth;
  return columnMonthLabel.startsWith(cardMonth);
}
